import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import Panel from '../ui/Panel';
import CategoryChip from '../ui/CategoryChip';

const JIRA_BROWSE = 'https://rollerdigital.atlassian.net/browse/';

/**
 * Inline effort editor for tickets missing an estimate, with an AI-assisted
 * single-ticket and batch estimation path.
 */
const EffortManagementSection = ({
  open,
  setOpen,
  tickets,
  visibleTickets,
  loadingTickets,
  onRefresh,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  editingTicketId,
  editedEffortValue,
  setEditedEffortValue,
  onStartEdit,
  onCancelEdit,
  onSave,
  savingEffort,
  onEstimateOne,
  onEstimateAll,
  batchProcessing,
  batchProgress,
}) => (
  <Panel
    title="Effort Management"
    subtitle="Manage effort estimations for tickets that don't have values yet."
    action={
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {open ? 'Hide' : 'Show'}
      </Button>
    }
  >
    <Collapse in={open} unmountOnExit>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          onClick={onRefresh}
          startIcon={loadingTickets ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          disabled={loadingTickets || batchProcessing}
        >
          Refresh List
        </Button>
        <Button
          variant="contained"
          onClick={onEstimateAll}
          startIcon={
            batchProcessing ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />
          }
          disabled={loadingTickets || batchProcessing || tickets.length === 0}
        >
          AI Estimate All ({tickets.length})
        </Button>
      </Stack>

      {batchProcessing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Processing {batchProgress}% complete…
          </Typography>
          <LinearProgress variant="determinate" value={batchProgress} sx={{ mt: 0.5 }} />
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 480, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Effort (hours)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTickets.length > 0 ? (
              visibleTickets.map((ticket) => {
                const editing = editingTicketId === ticket.key;
                return (
                  <TableRow key={ticket.key} hover>
                    <TableCell>
                      <Link
                        href={`${JIRA_BROWSE}${ticket.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {ticket.key}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>{ticket.summary}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {ticket.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <CategoryChip category={ticket.category || 'Uncategorized'} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {editing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editedEffortValue}
                          onChange={(e) => setEditedEffortValue(e.target.value)}
                          inputProps={{ min: 0, step: 0.5 }}
                          autoFocus
                          sx={{ width: 90 }}
                        />
                      ) : (
                        ticket.estimatedEffort || 'Not set'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editing ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onSave(ticket.key)}
                            disabled={savingEffort}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={onCancelEdit}
                            disabled={savingEffort}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            onClick={() => onStartEdit(ticket.key, ticket.estimatedEffort)}
                          >
                            Edit
                          </Button>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEstimateOne(ticket)}
                            disabled={savingEffort}
                            title="Estimate with AI"
                          >
                            <AutoFixHighIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tickets without effort estimates found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={tickets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Collapse>
  </Panel>
);

export default EffortManagementSection;
